import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import { WalletProvider } from './context/WalletContext';
import { kiffTheme, handlerTheme, adminTheme } from './theme/kiff';

// Import pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import HandlerDashboard from './pages/HandlerDashboard';
import PetProfiles from './pages/PetProfiles';
import BookService from './pages/BookService';
import Profile from './pages/Profile';

import PaymentsDashboard from './pages/PaymentsDashboard';
import WalletPage from './pages/Wallet';
import AdminDashboard from './pages/AdminDashboard';
import HandlerApply from './pages/HandlerApply';
import PaymentReturn from './components/PaymentReturn';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Navbar from './components/Navbar';
import RouteMeta from './components/RouteMeta';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './pages/NotFound';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { isHandlerSite, isOwnerSite } from './config/siteMode';

/**
 * Role theming happens HERE, at the router level, so switching surface
 * changes the ground colour within one paint (the design-system QA rule) —
 * not per component, where it would arrive piecemeal.
 *
 *   owner (default)  warm paper + cobalt        — the Kiff base
 *   admin (/admin/*) cool grey + violet         — deliberately unglamorous
 *   handler site     Kiff base, larger body     — dark dispatcher shell is a
 *                                                 tracked follow-up
 */
function ThemedApp() {
  const { pathname } = useLocation();

  const theme = isHandlerSite
    ? handlerTheme
    : pathname.startsWith('/admin')
      ? adminTheme
      : kiffTheme;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Renders nothing; syncs tab title + description with the route. */}
      <RouteMeta />
      <div className="App">
        <Navbar />
        <Routes>
          {/* Public */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          {/* Password reset. /reset-password is the URL the backend puts
              in the reset email, so its path must not change. */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Authenticated — shared across both sites */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute><PaymentsDashboard /></ProtectedRoute>} />

          {/* Owner site only */}
          {isOwnerSite && (
            <>
              <Route path="/owner/dashboard" element={<ProtectedRoute><OwnerDashboard /></ProtectedRoute>} />
              <Route path="/pets" element={<ProtectedRoute><PetProfiles /></ProtectedRoute>} />
              <Route path="/book-service" element={<ProtectedRoute><BookService /></ProtectedRoute>} />
              <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
              {/* Staff-only admin portal lives on the owner site */}
              <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            </>
          )}

          {/* Handler site only */}
          {isHandlerSite && (
            <>
              <Route path="/handler/dashboard" element={<ProtectedRoute><HandlerDashboard /></ProtectedRoute>} />
              <Route path="/handler/apply" element={<ProtectedRoute><HandlerApply /></ProtectedRoute>} />
            </>
          )}

          {/* Reached via redirect back from Stitch — must stay accessible. */}
          <Route path="/payment/return" element={<PaymentReturn />} />

          {/* Catch-all. Without this, an unknown URL matched no route and
              rendered a bare navbar over an empty page. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </ThemeProvider>
  );
}

function App() {
  return (
    // Outermost so a crash in any provider or page still renders something
    // the user can act on, rather than a white screen.
    <ErrorBoundary>
      <AuthProvider>
        <WalletProvider>
          <Router>
            <ThemedApp />
          </Router>
        </WalletProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
